import { useState, useCallback } from 'react';

export type ErrorType = 'network' | 'processing' | 'validation' | 'pdf' | 'unknown';

export interface SmartError {
  type: ErrorType;
  message: string;
  icon: string;
  color: string;
  suggestions: string[];
  canRetry: boolean;
  autoRetry: boolean;
  retryDelay?: number;
}

export interface ErrorRecoveryState {
  currentError: SmartError | null;
  attemptCount: number;
  isRetrying: boolean;
  lastErrorTime: number;
}

const ERROR_CONFIGS: Record<ErrorType, Omit<SmartError, 'message'>> = {
  network: {
    type: 'network',
    icon: '🌐',
    color: 'text-amber-600',
    suggestions: ['Verifique sua conexão com a internet', 'Tente novamente em alguns segundos'],
    canRetry: true,
    autoRetry: true,
    retryDelay: 2000
  },
  processing: {
    type: 'processing',
    icon: '⚙️',
    color: 'text-orange-600',
    suggestions: ['Os dados estão sendo processados', 'Aguarde um momento e tente novamente'],
    canRetry: true,
    autoRetry: true,
    retryDelay: 3000
  },
  validation: {
    type: 'validation',
    icon: '⚠️',
    color: 'text-yellow-600',
    suggestions: ['Verifique se todos os campos estão preenchidos', 'Confira os dados inseridos'],
    canRetry: true,
    autoRetry: false
  },
  pdf: {
    type: 'pdf',
    icon: '📄',
    color: 'text-blue-600',
    suggestions: ['Tentando gerar o documento novamente', 'Verifique se há espaço disponível'],
    canRetry: true,
    autoRetry: true,
    retryDelay: 1500
  },
  unknown: {
    type: 'unknown',
    icon: '🤔',
    color: 'text-gray-600',
    suggestions: ['Ocorreu um problema inesperado', 'Seus dados estão seguros'],
    canRetry: true,
    autoRetry: false
  }
};

const ERROR_MESSAGES: Record<ErrorType, string[]> = {
  network: [
    'Ops! Parece que sua internet está instável',
    'Conexão perdida. Vamos tentar reconectar...',
    'Problema de rede detectado. Aguarde um momento...'
  ],
  processing: [
    'Processando seu pedido com cuidado...',
    'Quase lá! Finalizando os últimos detalhes...',
    'Nossos servidores estão ocupados. Tentando novamente...'
  ],
  validation: [
    'Alguns dados precisam ser revisados',
    'Vamos verificar essas informações juntos',
    'Pequeno ajuste necessário nos dados'
  ],
  pdf: [
    'Preparando seu documento PDF...',
    'Gerando relatório personalizado...',
    'Problema na geração do PDF. Tentando método alternativo...'
  ],
  unknown: [
    'Algo inesperado aconteceu, mas vamos resolver!',
    'Encontramos um obstáculo. Não se preocupe!',
    'Situação incomum detectada. Investigando...'
  ]
};

export const useSmartErrorRecovery = () => {
  const [state, setState] = useState<ErrorRecoveryState>({
    currentError: null,
    attemptCount: 0,
    isRetrying: false,
    lastErrorTime: 0
  });

  const getErrorMessage = useCallback((type: ErrorType, attemptCount: number): string => {
    const messages = ERROR_MESSAGES[type];
    const messageIndex = Math.min(attemptCount, messages.length - 1);
    return messages[messageIndex];
  }, []);

  const detectErrorType = useCallback((error: Error | string): ErrorType => {
    const errorMessage = typeof error === 'string' ? error : error.message;
    const lowerMessage = errorMessage.toLowerCase();

    if (lowerMessage.includes('network') || lowerMessage.includes('fetch') || lowerMessage.includes('connection')) {
      return 'network';
    }
    if (lowerMessage.includes('pdf') || lowerMessage.includes('document')) {
      return 'pdf';
    }
    if (lowerMessage.includes('validation') || lowerMessage.includes('required') || lowerMessage.includes('invalid')) {
      return 'validation';
    }
    if (lowerMessage.includes('processing') || lowerMessage.includes('timeout')) {
      return 'processing';
    }
    return 'unknown';
  }, []);

  const handleError = useCallback((error: Error | string, customType?: ErrorType) => {
    const errorType = customType || detectErrorType(error);
    const config = ERROR_CONFIGS[errorType];
    const newAttemptCount = state.attemptCount + 1;
    
    const smartError: SmartError = {
      ...config,
      message: getErrorMessage(errorType, newAttemptCount)
    };

    setState(prev => ({
      ...prev,
      currentError: smartError,
      attemptCount: newAttemptCount,
      lastErrorTime: Date.now()
    }));

    return smartError;
  }, [state.attemptCount, detectErrorType, getErrorMessage]);

  const startRetry = useCallback(() => {
    setState(prev => ({
      ...prev,
      isRetrying: true
    }));
  }, []);

  const completeRetry = useCallback(() => {
    setState(prev => ({
      ...prev,
      isRetrying: false
    }));
  }, []);

  const reset = useCallback(() => {
    setState({
      currentError: null,
      attemptCount: 0,
      isRetrying: false,
      lastErrorTime: 0
    });
  }, []);

  const shouldAutoRetry = useCallback((): boolean => {
    if (!state.currentError?.autoRetry) return false;
    if (state.attemptCount >= 3) return false;
    if (state.isRetrying) return false;
    
    const timeSinceLastError = Date.now() - state.lastErrorTime;
    const minDelay = state.currentError.retryDelay || 2000;
    
    return timeSinceLastError >= minDelay;
  }, [state]);

  const getRetryDelay = useCallback((): number => {
    if (!state.currentError?.retryDelay) return 2000;
    
    // Progressive delay: base delay * attempt count
    return state.currentError.retryDelay * state.attemptCount;
  }, [state.currentError, state.attemptCount]);

  const getContextualActions = useCallback(() => {
    if (!state.currentError) return [];

    const actions = ['Tentar Novamente'];
    
    if (state.attemptCount >= 2) {
      actions.push('Salvar Rascunho');
    }
    
    if (state.attemptCount >= 3) {
      actions.push('Reportar Problema');
    }

    return actions;
  }, [state.currentError, state.attemptCount]);

  return {
    state,
    handleError,
    startRetry,
    completeRetry,
    reset,
    shouldAutoRetry,
    getRetryDelay,
    getContextualActions,
    detectErrorType
  };
};